// client/src/pages/ExpertIntake.tsx - FIXED VERSION
// Fixed file uploads to work properly with Supabase storage
import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import StateSelect from '../components/StateSelect';
import { useNavigate } from 'react-router-dom';

// Medical specialties list for standardization
const MEDICAL_SPECIALTIES = [
    { value: 'anesthesiology', label: 'Anesthesiology', subSpecialties: ['Pain Management', 'Critical Care Anesthesiology', 'Pediatric Anesthesiology', 'Obstetric Anesthesiology'] },
    { value: 'cardiology', label: 'Cardiology', subSpecialties: ['Interventional Cardiology', 'Electrophysiology', 'Heart Failure', 'Pediatric Cardiology'] },
    { value: 'dermatology', label: 'Dermatology', subSpecialties: ['Mohs Surgery', 'Pediatric Dermatology', 'Cosmetic Dermatology', 'Immunodermatology'] },
    { value: 'emergency_medicine', label: 'Emergency Medicine', subSpecialties: ['Pediatric Emergency Medicine', 'Sports Medicine', 'Medical Toxicology', 'Wilderness Medicine'] },
    { value: 'endocrinology', label: 'Endocrinology', subSpecialties: ['Diabetes', 'Thyroid Disorders', 'Metabolic Bone Disease', 'Reproductive Endocrinology'] },
    { value: 'family_medicine', label: 'Family Medicine', subSpecialties: ['Sports Medicine', 'Geriatric Medicine', 'Adolescent Medicine', 'Hospice and Palliative Medicine'] },
    { value: 'gastroenterology', label: 'Gastroenterology', subSpecialties: ['Hepatology', 'Inflammatory Bowel Disease', 'Pancreatology', 'Motility Disorders'] },
    { value: 'internal_medicine', label: 'Internal Medicine', subSpecialties: ['Hospital Medicine', 'Geriatric Medicine', 'Sports Medicine', 'Clinical Informatics'] },
    { value: 'neurology', label: 'Neurology', subSpecialties: ['Stroke Neurology', 'Epilepsy', 'Movement Disorders', 'Neuromuscular Medicine'] },
    { value: 'obstetrics_gynecology', label: 'Obstetrics & Gynecology', subSpecialties: ['Maternal-Fetal Medicine', 'Reproductive Endocrinology', 'Gynecologic Oncology', 'Urogynecology'] },
    { value: 'oncology', label: 'Oncology', subSpecialties: ['Medical Oncology', 'Radiation Oncology', 'Pediatric Oncology', 'Surgical Oncology'] },
    { value: 'orthopedics', label: 'Orthopedics', subSpecialties: ['Spine Surgery', 'Joint Replacement', 'Sports Medicine', 'Pediatric Orthopedics'] },
    { value: 'pediatrics', label: 'Pediatrics', subSpecialties: ['Pediatric Neurology', 'Pediatric Cardiology', 'Neonatology', 'Pediatric Emergency Medicine'] },
    { value: 'psychiatry', label: 'Psychiatry', subSpecialties: ['Forensic Psychiatry', 'Child Psychiatry', 'Addiction Psychiatry', 'Geriatric Psychiatry'] },
    { value: 'radiology', label: 'Radiology', subSpecialties: ['Neuroradiology', 'Interventional Radiology', 'Pediatric Radiology', 'Nuclear Radiology'] },
    { value: 'surgery', label: 'Surgery', subSpecialties: ['Trauma Surgery', 'Vascular Surgery', 'Plastic Surgery', 'Pediatric Surgery'] },
    { value: 'urology', label: 'Urology', subSpecialties: ['Pediatric Urology', 'Urologic Oncology', 'Female Urology', 'Male Infertility'] },
];

// Add comprehensive nursing specialties
const NURSING_SPECIALTIES = {
    value: 'nursing',
    label: 'Nursing',
    subSpecialties: [
        'Critical Care Nursing',
        'Emergency Room Nursing',
        'Perioperative/OR Nursing',
        'Medical-Surgical Nursing',
        'Pediatric Nursing',
        'Neonatal/NICU Nursing',
        'Obstetric/Labor & Delivery',
        'Psychiatric/Mental Health Nursing',
        'Geriatric Nursing',
        'Oncology Nursing',
        'Cardiac/Telemetry Nursing',
        'Rehabilitation Nursing',
        'Home Health Nursing',
        'School Nursing',
        'Infection Control',
        'Wound Care Specialist',
        'Pain Management Nursing',
        'Dialysis Nursing',
        'Hospice/Palliative Care',
        'Case Management',
        'Nurse Educator',
        'Nurse Administrator',
        'Legal Nurse Consultant',
        'Forensic Nursing',
        'Flight/Transport Nursing',
        'Occupational Health Nursing',
        'Public Health Nursing',
        'Nurse Practitioner - Family',
        'Nurse Practitioner - Adult',
        'Nurse Practitioner - Pediatric',
        'Nurse Practitioner - Psychiatric',
        'Clinical Nurse Specialist',
        'Certified Nurse Midwife',
        'Certified Registered Nurse Anesthetist'
    ]
};

// Add after line 36 (after MEDICAL_SPECIALTIES)
const ALL_SPECIALTIES = [
    ...MEDICAL_SPECIALTIES,
    NURSING_SPECIALTIES,
    {
        value: 'therapy',
        label: 'Therapy',
        subSpecialties: [
            'Physical Therapy',
            'Occupational Therapy',
            'Speech Therapy',
            'Respiratory Therapy',
            'Massage Therapy',
            'Behavioral Therapy'
        ]
    }
];

export default function ExpertIntake() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [hasProfile, setHasProfile] = useState(false);
    const [userEmail, setUserEmail] = useState('');

    // Form state
    const [fullName, setFullName] = useState('');
    const [npi, setNpi] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [phone, setPhone] = useState('');
    const [socialUrl, setSocialUrl] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [statesLicensed, setStatesLicensed] = useState<string[]>([]);
    const [specialty, setSpecialty] = useState('');
    const [subSpecialty, setSubSpecialty] = useState('');
    const [certifications, setCertifications] = useState<string[]>([]);
    const [currentlyPracticing, setCurrentlyPracticing] = useState(true);
    const [yearsPracticing, setYearsPracticing] = useState<string>('');
    const [publishedScholarly, setPublishedScholarly] = useState(false);
    const [numDefenseDeps, setNumDefenseDeps] = useState<string>('');
    const [numPlaintiffDeps, setNumPlaintiffDeps] = useState<string>('');
    const [numDefenseTrials, setNumDefenseTrials] = useState<string>('');
    const [numPlaintiffTrials, setNumPlaintiffTrials] = useState<string>('');
    const [retainerFee, setRetainerFee] = useState<string>('');
    const [rateReview, setRateReview] = useState<string>('');
    const [rateDeposition, setRateDeposition] = useState<string>('');
    const [rateTrial, setRateTrial] = useState<string>('');
    const [daubertHistory, setDaubertHistory] = useState<string>('never_challenged');
    const [casesWon, setCasesWon] = useState<string>('');
    const [casesLost, setCasesLost] = useState<string>('');
    const [casesSettled, setCasesSettled] = useState<string>('');
    const [casesPending, setCasesPending] = useState<string>('');
    const [languages, setLanguages] = useState<string[]>(['English']);
    const [remoteTestimony, setRemoteTestimony] = useState<boolean>(true);
    const [priorLicenseActions, setPriorLicenseActions] = useState(false);
    const [licenseDetails, setLicenseDetails] = useState('');
    const [currentLawsuit, setCurrentLawsuit] = useState(false);
    const [lawsuitDetails, setLawsuitDetails] = useState('');
    const [cvFile, setCvFile] = useState<File | null>(null);
    const [feeFile, setFeeFile] = useState<File | null>(null);
    const [profilePic, setProfilePic] = useState<File | null>(null);
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});

    // Add after line 96 (in form state)
    const [expertType, setExpertType] = useState<'physician' | 'nurse' | 'therapist'>('physician');
    const [experienceCases, setExperienceCases] = useState('');
    const [additionalDocsFile, setAdditionalDocsFile] = useState<File | null>(null);
    const [additionalDocsFiles, setAdditionalDocsFiles] = useState<File[]>([]);
    const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);

    // Add keyword suggestions based on specialty
    const SPECIALTY_KEYWORDS = {
        cardiology: ['cardiac catheterization', 'echocardiogram', 'stent placement', 'heart failure management', 'arrhythmia', 'coronary artery disease'],
        neurology: ['stroke protocol', 'seizure management', 'EEG interpretation', 'multiple sclerosis', 'parkinsons disease', 'neuropathy'],
        orthopedics: ['joint replacement', 'arthroscopy', 'fracture care', 'spinal fusion', 'sports injuries', 'bone infection'],
        nursing: ['medication administration', 'patient assessment', 'wound care', 'infection control', 'patient education', 'care coordination'],
        therapy: ['functional assessment', 'treatment planning', 'rehabilitation protocols', 'patient mobility', 'therapeutic exercises', 'outcomes measurement']
    };

    const selectedSpecialty = ALL_SPECIALTIES.find(s => s.value === specialty);
    const availableSubSpecialties = selectedSpecialty?.subSpecialties || [];

    const [referralSource, setReferralSource] = useState('');

    useEffect(() => {
        checkExistingProfile();
    }, []);

    const checkExistingProfile = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                navigate('/login');
                return;
            }
            setUserEmail(user.email || '');

            const { data: expertData } = await supabase
                .from('experts')
                .select('id')
                .eq('user_id', user.id)
                .single();

            if (expertData) {
                setHasProfile(true);
                navigate('/expert-dashboard');
                return;
            }

            const userType = user.user_metadata?.user_type;
            if (userType !== 'expert') {
                navigate('/');
                return;
            }

            setLoading(false);
        } catch {
            setLoading(false);
        }
    };



    const clearFile = (fileType: 'cv' | 'fee' | 'profile') => {
        if (fileType === 'cv') {
            setCvFile(null);
            const inp = document.getElementById('cv-upload') as HTMLInputElement;
            if (inp) inp.value = '';
        }
        if (fileType === 'fee') {
            setFeeFile(null);
            const inp = document.getElementById('fee-upload') as HTMLInputElement;
            if (inp) inp.value = '';
        }
        if (fileType === 'profile') {
            setProfilePic(null);
            const inp = document.getElementById('profile-upload') as HTMLInputElement;
            if (inp) inp.value = '';
        }
        setUploadProgress(prev => {
            const upd = { ...prev };
            delete upd[fileType];
            return upd;
        });
    };

    const uploadFile = async (file: File, bucket: string, folder: string, fileType: string): Promise<string | null> => {
        try {
            console.log(`⬆️ Starting upload: ${file.name} to ${bucket}/${folder}`);
            setUploadProgress(p => ({ ...p, [fileType]: 0 }));
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const ts = Date.now();
            const ext = file.name.split('.').pop();
            const filename = `${user.id}-${ts}.${ext}`;
            const path = `${folder}/${filename}`;
            console.log(`⬆️ Upload path: ${path}`);

            // Skip bucket creation check - bucket should already exist
            // If it doesn't, the upload will fail with a clear error

            console.log(`⬆️ Uploading to bucket: ${bucket}`);
            const { data: upData, error: upErr } = await supabase.storage.from(bucket).upload(path, file, {
                cacheControl: '3600',
                upsert: true
            });

            if (upErr) {
                console.error(`❌ Upload error for ${file.name}:`, upErr);
                throw upErr;
            }

            console.log(`✅ Upload successful:`, upData);
            setUploadProgress(p => ({ ...p, [fileType]: 100 }));
            const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(upData.path);
            console.log(`✅ Public URL:`, urlData.publicUrl);
            return urlData.publicUrl;
        } catch (err: any) {
            console.error(`❌ UPLOAD FAILED for ${fileType}:`, err);
            setUploadProgress(p => {
                const upd = { ...p };
                delete upd[fileType];
                return upd;
            });
            setMessage(prev => prev + `\n❌ Upload failed for ${file.name}: ${err.message}`);
            return null;
        }
    };

    const getFileSizeConfig = (bucket: string) => {
        if (bucket === 'cv-uploads') {
            return {
                allowedTypes: [
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                ],
                maxSize: 10 * 1024 * 1024
            };
        }
        if (bucket === 'fee-schedules') {
            return { allowedTypes: ['application/pdf'], maxSize: 5 * 1024 * 1024 };
        }
        if (bucket === 'profile-pictures') {
            return {
                allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
                maxSize: 2 * 1024 * 1024
            };
        }
        if (bucket === 'additional-docs') {
            return {
                allowedTypes: [
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                ],
                maxSize: 10 * 1024 * 1024
            };
        }
        return { allowedTypes: ['*'], maxSize: 10 * 1024 * 1024 };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setMessage('');
        try {
            const { data, error: ue } = await supabase.auth.getUser();
            if (ue || !data.user) throw new Error('Not logged in');

            const uid = data.user.id;
            const email = data.user.email || '';
            let cvUrl = '',
                feeUrl = '',
                picUrl = '';

            if (cvFile) cvUrl = (await uploadFile(cvFile, 'cv-uploads', 'documents', 'cv')) || '';
            if (feeFile) feeUrl = (await uploadFile(feeFile, 'fee-schedules', 'documents', 'fee')) || '';
            if (profilePic) picUrl = (await uploadFile(profilePic, 'profile-pictures', 'images', 'profile')) || '';

            let addDocsUrl: string | null = null;
            // Upload multiple additional docs and store URLs as JSON array
            console.log('📁 Additional docs files to upload:', additionalDocsFiles.length);
            if (additionalDocsFiles.length > 0) {
                const uploadedUrls: string[] = [];
                for (let i = 0; i < additionalDocsFiles.length; i++) {
                    const file = additionalDocsFiles[i];
                    console.log(`📁 Uploading additional doc ${i + 1}:`, file.name);
                    const url = await uploadFile(file, 'additional-docs', 'documents', `additionalDocs_${i}`);
                    console.log(`📁 Upload result for ${file.name}:`, url);
                    if (url) uploadedUrls.push(url);
                }
                console.log('📁 All uploaded URLs:', uploadedUrls);
                if (uploadedUrls.length > 0) {
                    addDocsUrl = uploadedUrls.length === 1 ? uploadedUrls[0] : JSON.stringify(uploadedUrls);
                }
                console.log('📁 Final addDocsUrl to save:', addDocsUrl);
            } else if (additionalDocsFile) {
                // Fallback for single file
                console.log('📁 Using single file fallback:', additionalDocsFile.name);
                addDocsUrl = await uploadFile(additionalDocsFile, 'additional-docs', 'documents', 'additionalDocs');
                console.log('📁 Single file upload result:', addDocsUrl);
            }

            const expertData = {
                user_id: uid,
                full_name: fullName,
                npi,
                job_title: jobTitle,
                phone_number: phone,
                email,
                social_url: socialUrl || null,
                website_url: websiteUrl || null,
                states_licensed: statesLicensed,
                specialty: ALL_SPECIALTIES.find(s => s.value === specialty)?.label || specialty,
                sub_specialty: subSpecialty || null,
                certifications,
                currently_practicing: currentlyPracticing,
                years_practicing: Number(yearsPracticing) || 0,
                published_scholarly: publishedScholarly,
                num_defense_depositions: Number(numDefenseDeps) || 0,
                num_plaintiff_depositions: Number(numPlaintiffDeps) || 0,
                num_defense_trials: Number(numDefenseTrials) || 0,
                num_plaintiff_trials: Number(numPlaintiffTrials) || 0,
                retainer_fee: Number(retainerFee) || 0,
                rate_review: Number(rateReview) || 0,
                rate_deposition: Number(rateDeposition) || 0,
                rate_trial: Number(rateTrial) || 0,
                expert_type: expertType,
                selected_keywords: selectedKeywords,
                case_experience: experienceCases,
                additional_docs_url: addDocsUrl,
                availability: null,
                referral_sources: referralSource || null,
                prior_license_actions: priorLicenseActions,
                license_action_details: priorLicenseActions ? licenseDetails : null,
                current_lawsuit: currentLawsuit,
                lawsuit_details: currentLawsuit ? lawsuitDetails : null,
                cv_url: cvUrl || null,
                fee_schedule_url: feeUrl || null,
                profile_picture_url: picUrl || null,
                daubert_challenge_history: daubertHistory,
                cases_won: Number(casesWon) || 0,
                cases_lost: Number(casesLost) || 0,
                cases_settled: Number(casesSettled) || 0,
                cases_pending: Number(casesPending) || 0,
                languages: languages,
                remote_testimony: remoteTestimony,
                is_verified: false
            };

            const { error: insertErr } = await supabase.from('experts').insert([expertData]);
            if (insertErr) throw insertErr;


            // Notify admin of new user
            try {
                const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8001').trim();
                await fetch(`${apiUrl}/notify/new-user`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_name: fullName,
                        user_email: userEmail,
                        user_type: 'expert'
                    })
                });
            } catch (e) {}

            setMessage('✅ Profile submitted successfully! Your account is under review and will be verified within 2-3 business days. Redirecting to your dashboard...');
            setUploadProgress({});
            setTimeout(() => window.location.href = '/expert-dashboard', 3000);
        } catch (err: any) {
            setMessage(`Error: ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }
    if (hasProfile) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center text-gray-600">You already have an expert profile. Redirecting...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-6">
                        <h2 className="text-3xl font-bold text-white">Expert Witness Registration</h2>
                        <p className="text-blue-100 mt-2">Join our network of verified medical experts</p>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 m-6">
                        <div className="flex">
                            <svg className="h-5 w-5 text-blue-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <p className="ml-3 text-sm text-blue-700">
                                <strong>Verification Process:</strong> After submitting your application, our team will review and verify your credentials within 2-3 business days.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-8 space-y-8">
                        {/* Personal Information */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Personal Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={fullName}
                                        onChange={e => setFullName(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Dr. John Smith"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        NPI / License Number
                                    </label>
                                    <input
                                        type="text"
                                        value={npi}
                                        onChange={e => setNpi(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="1234567890"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Job Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={jobTitle}
                                        onChange={e => setJobTitle(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Chief of Cardiology"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Profile Picture
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="profile-upload"
                                            type="file"
                                            accept="image/*"
                                            onChange={e => setProfilePic(e.target.files?.[0] || null)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                        {profilePic && (
                                            <button
                                                type="button"
                                                onClick={() => clearFile('profile')}
                                                className="absolute right-2 top-2 text-red-500 hover:text-red-700"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                    {profilePic && (
                                        <p className="mt-1 text-sm text-green-600">✓ {profilePic.name}</p>
                                    )}
                                    {uploadProgress.profile !== undefined && (
                                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                            <div
                                                className="bg-blue-600 h-2 rounded-full"
                                                style={{ width: `${uploadProgress.profile}%` }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Case Experience */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Case Experience</h3>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Describe your expert witness case experience
                                </label>
                                <textarea
                                    value={experienceCases}
                                    onChange={e => setExperienceCases(e.target.value)}
                                    rows={6}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                    placeholder="Please describe specific cases you've been involved in as an expert witness..."
                                />
                            </div>
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Upload Additional Documentation (Optional)
                                </label>

                                {/* File list with remove buttons */}
                                {additionalDocsFiles.length > 0 && (
                                    <div className="mb-3 space-y-2">
                                        {additionalDocsFiles.map((file, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg">
                                                <span className="text-sm text-green-700 flex items-center">
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    {file.name}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newFiles = additionalDocsFiles.filter((_, i) => i !== idx);
                                                        setAdditionalDocsFiles(newFiles);
                                                        setAdditionalDocsFile(newFiles[0] || null);
                                                    }}
                                                    className="text-red-500 hover:text-red-700 p-1"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Add file button */}
                                {additionalDocsFiles.length < 10 && (
                                    <div className="flex items-center space-x-3">
                                        <label className="flex-1 cursor-pointer">
                                            <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                                                <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                <span className="text-sm text-gray-600">
                                                    {additionalDocsFiles.length === 0 ? 'Add Document' : 'Add Another Document'}
                                                </span>
                                            </div>
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx"
                                                onChange={e => {
                                                    const file = e.target.files?.[0];
                                                    console.log('📎 File selected:', file?.name, file?.type, file?.size);
                                                    if (file) {
                                                        if (additionalDocsFiles.length < 10) {
                                                            const newFiles = [...additionalDocsFiles, file];
                                                            setAdditionalDocsFiles(newFiles);
                                                            setAdditionalDocsFile(newFiles[0]);
                                                            console.log('📎 Files in state now:', newFiles.map(f => f.name));
                                                        }
                                                    }
                                                    // Reset input so same file can be selected again
                                                    e.target.value = '';
                                                }}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                )}

                                <p className="text-xs text-gray-500 mt-2">
                                    Upload any additional documentation about your expert witness experience.
                                    PDF, DOC, or DOCX format. Maximum 10 documents.
                                    {additionalDocsFiles.length > 0 && (
                                        <span className="ml-1 font-medium">({additionalDocsFiles.length}/10 files added)</span>
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Contact Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email (from your account)
                                    </label>
                                    <input
                                        type="email"
                                        disabled
                                        value={userEmail}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        This is your login email and cannot be changed here
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Phone <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        required
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="(555) 123-4567"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        States Licensed <span className="text-red-500">*</span>
                                    </label>
                                    <StateSelect
                                        value={statesLicensed}
                                        onChange={setStatesLicensed}
                                        multiple
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Hold Ctrl/Cmd to select multiple states
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        LinkedIn/Social Profile
                                    </label>
                                    <input
                                        type="url"
                                        value={socialUrl}
                                        onChange={e => setSocialUrl(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="https://linkedin.com/in/yourprofile"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Professional Website
                                    </label>
                                    <input
                                        type="url"
                                        value={websiteUrl}
                                        onChange={e => setWebsiteUrl(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="https://yourpractice.com"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Professional Information */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Professional Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Specialty <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        required
                                        value={specialty}
                                        onChange={e => {
                                            setSpecialty(e.target.value);
                                            setSubSpecialty('');
                                        }}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Select a specialty</option>
                                        {ALL_SPECIALTIES.map(spec => (
                                            <option key={spec.value} value={spec.value}>
                                                {spec.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Sub-specialty
                                    </label>
                                    <select
                                        value={subSpecialty}
                                        onChange={e => setSubSpecialty(e.target.value)}
                                        disabled={!specialty}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                    >
                                        <option value="">Select a sub-specialty (optional)</option>
                                        {availableSubSpecialties.map(sub => (
                                            <option key={sub} value={sub}>
                                                {sub}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Years Practicing <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        value={yearsPracticing}
                                        onChange={e => setYearsPracticing(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Certifications
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., MD, FACC, ABIM (comma separated)"
                                        value={certifications.join(', ')}
                                        onChange={e =>
                                            setCertifications(
                                                e
                                                    .target
                                                    .value
                                                    .split(',')
                                                    .map(s => s.trim())
                                                    .filter(s => s)
                                            )
                                        }
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Experience & Practice */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Experience & Practice</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={currentlyPracticing}
                                        onChange={e => setCurrentlyPracticing(e.target.checked)}
                                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Currently Practicing</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={publishedScholarly}
                                        onChange={e => setPublishedScholarly(e.target.checked)}
                                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Published Scholarly Works</span>
                                </label>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    { label: 'Defense Depositions', value: numDefenseDeps, setter: setNumDefenseDeps },
                                    { label: 'Plaintiff Depositions', value: numPlaintiffDeps, setter: setNumPlaintiffDeps },
                                    { label: 'Defense Trials', value: numDefenseTrials, setter: setNumDefenseTrials },
                                    { label: 'Plaintiff Trials', value: numPlaintiffTrials, setter: setNumPlaintiffTrials },
                                ].map((field, idx) => (
                                    <div key={idx}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={field.value}
                                            onChange={e => field.setter(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="0"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Fees & Rates */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Fees & Rates</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {[
                                    { label: 'Retainer Fee ($)', value: retainerFee, setter: setRetainerFee },
                                    { label: 'Hourly Review Rate ($/hr)', value: rateReview, setter: setRateReview },
                                    { label: 'Deposition Rate ($/hr)', value: rateDeposition, setter: setRateDeposition },
                                    { label: 'Trial Rate ($/hr)', value: rateTrial, setter: setRateTrial },
                                ].map((field, idx) => (
                                    <div key={idx}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{field.label} <span className="text-red-500">*</span></label>
                                        <input
                                            type="number"
                                            required
                                            min="0"
                                            value={field.value}
                                            onChange={e => field.setter(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="0"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Daubert/Frye Challenge History */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Expert Witness History</h3>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Have you ever been challenged as an expert witness (Daubert/Frye motion)?
                                    </label>
                                    <select
                                        value={daubertHistory}
                                        onChange={e => setDaubertHistory(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="never_challenged">Never challenged</option>
                                        <option value="challenged_admitted">Challenged — testimony admitted</option>
                                        <option value="challenged_excluded">Challenged — testimony excluded</option>
                                        <option value="challenged_mixed">Multiple challenges — mixed results</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Approximate Case Outcomes
                                    </label>
                                    <p className="text-xs text-gray-500 mb-3">Of the cases you've worked on as an expert, approximately how many resulted in each outcome?</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {[
                                            { label: 'Won', value: casesWon, setter: setCasesWon },
                                            { label: 'Lost', value: casesLost, setter: setCasesLost },
                                            { label: 'Settled', value: casesSettled, setter: setCasesSettled },
                                            { label: 'Pending', value: casesPending, setter: setCasesPending },
                                        ].map((field, idx) => (
                                            <div key={idx}>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={field.value}
                                                    onChange={e => field.setter(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="0"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Available for Remote/Video Testimony?
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="remote" checked={remoteTestimony === true} onChange={() => setRemoteTestimony(true)} className="h-4 w-4 text-blue-600" />
                                            <span className="text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="remote" checked={remoteTestimony === false} onChange={() => setRemoteTestimony(false)} className="h-4 w-4 text-blue-600" />
                                            <span className="text-gray-700">No — in-person only</span>
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Languages Spoken Fluently
                                    </label>
                                    <div className="flex flex-wrap gap-3">
                                        {['English', 'Spanish', 'Mandarin', 'French', 'Arabic', 'Portuguese', 'Russian', 'Korean', 'Vietnamese', 'Tagalog'].map(lang => (
                                            <label key={lang} className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={languages.includes(lang)}
                                                    onChange={e => {
                                                        if (e.target.checked) {
                                                            setLanguages([...languages, lang]);
                                                        } else {
                                                            setLanguages(languages.filter(l => l !== lang));
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-blue-600 rounded"
                                                />
                                                <span className="text-sm text-gray-700">{lang}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Expert Type Selection */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Expert Type</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[
                                    { type: 'physician', title: 'Physician', desc: 'Medical doctor (MD/DO)' },
                                    { type: 'nurse',     title: 'Nurse',     desc: 'RN, NP, or other nursing professional' },
                                    { type: 'therapist', title: 'Therapist', desc: 'PT, OT, ST, or other therapy professional' },
                                ].map((opt) => (
                                    <button
                                        key={opt.type}
                                        type="button"
                                        onClick={() => setExpertType(opt.type as any)}
                                        className={`p-4 rounded-lg border-2 transition ${
                                            expertType === opt.type ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                                        }`}
                                    >
                                        <h4 className="font-semibold">{opt.title}</h4>
                                        <p className="text-sm text-gray-600">{opt.desc}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Legal Disclosures */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Legal Disclosures</h3>
                            <div className="space-y-6">
                                <div>
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={priorLicenseActions}
                                            onChange={e => setPriorLicenseActions(e.target.checked)}
                                            className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Have you had any prior license actions?</span>
                                    </label>
                                    {priorLicenseActions && (
                                        <textarea
                                            placeholder="Please describe any prior license actions, including dates and resolutions"
                                            value={licenseDetails}
                                            onChange={e => setLicenseDetails(e.target.value)}
                                            className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            rows={3}
                                        />
                                    )}
                                </div>
                                <div>
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={currentLawsuit}
                                            onChange={e => setCurrentLawsuit(e.target.checked)}
                                            className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Are you currently involved in any lawsuits?</span>
                                    </label>
                                    {currentLawsuit && (
                                        <textarea
                                            placeholder="Please describe any current lawsuits, including case type and status"
                                            value={lawsuitDetails}
                                            onChange={e => setLawsuitDetails(e.target.value)}
                                            className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            rows={3}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Document Uploads */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Document Uploads (Optional)</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Curriculum Vitae (CV)</label>
                                    <div className="relative">
                                        <input
                                            id="cv-upload"
                                            type="file"
                                            accept=".pdf,.doc,.docx"
                                            onChange={e => setCvFile(e.target.files?.[0] || null)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                        {cvFile && (
                                            <button
                                                type="button"
                                                onClick={() => clearFile('cv')}
                                                className="absolute right-2 top-2 text-red-500 hover:text-red-700"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">PDF, DOC, or DOCX format (max 10MB)</p>
                                    {cvFile && <p className="mt-1 text-sm text-green-600">✓ {cvFile.name}</p>}
                                    {uploadProgress.cv !== undefined && (
                                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${uploadProgress.cv}%` }} />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Fee Schedule</label>
                                    <div className="relative">
                                        <input
                                            id="fee-upload"
                                            type="file"
                                            accept=".pdf"
                                            onChange={e => setFeeFile(e.target.files?.[0] || null)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                        {feeFile && (
                                            <button
                                                type="button"
                                                onClick={() => clearFile('fee')}
                                                className="absolute right-2 top-2 text-red-500 hover:text-red-700"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">PDF format only (max 5MB)</p>
                                    {feeFile && <p className="mt-1 text-sm text-green-600">✓ {feeFile.name}</p>}
                                    {uploadProgress.fee !== undefined && (
                                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${uploadProgress.fee}%` }} />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-700">
                                    <strong>Note:</strong> If uploads fail, your profile will still be created—you can upload files later via your dashboard.
                                </p>
                            </div>
                        </div>


                        {/* Referral Source */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">How Did You Hear About Us?</h3>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Where did you hear about Expert Link Pro?
                                </label>
                                <textarea
                                    value={referralSource}
                                    onChange={e => setReferralSource(e.target.value)}
                                    rows={2}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Colleague referral, online search, legal conference, social media, etc."
                                />
                            </div>
                        </div>

                        {/* Terms & Conditions */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <label className="flex items-start">
                                <input
                                    type="checkbox"
                                    required
                                    className="mt-1 mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">
                                    I certify all information is accurate. I understand Expert Link Pro will verify my credentials and that false information may result in removal. I acknowledge a 2-3 business day review period, which may or may not affect the features I have access to.
                                </span>
                            </label>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-6 flex flex-col space-y-4">
                            <button
                                type="submit"
                                disabled={submitting}
                                className={`w-full py-3 px-4 rounded-lg text-white font-medium transition duration-200 ${
                                    submitting
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700 transform hover:scale-[1.02]'
                                }`}
                            >
                                {submitting ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Submitting Registration...
                                    </span>
                                ) : (
                                    'Submit Registration'
                                )}
                            </button>
                        </div>

                        {/* Feedback Message */}
                        {message && (
                            <div
                                className={`mt-4 p-4 rounded-lg flex items-start ${
                                    message.includes('✅')
                                        ? 'bg-green-50 border border-green-200 text-green-700'
                                        : message.includes('Warning')
                                            ? 'bg-yellow-50 border border-yellow-200 text-yellow-700'
                                            : 'bg-red-50 border border-red-200 text-red-700'
                                }`}
                            >
                                <span className="whitespace-pre-line">{message}</span>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}
